import React, {useCallback, useEffect, useState} from "react";
import {Box, Stack, Typography, IconButton, Tooltip, Button} from "@mui/material";
import {useAtom, useSetAtom} from "jotai";
import {HugeiconsIcon} from "@hugeicons/react";
import {Refresh01Icon, Add01Icon} from "@hugeicons/core-free-icons";
import {toast} from "react-toastify";
import {KnowledgeManagerRepository} from "../../../repositories/KnowledgeManagerRepository";
import {StatStrip} from "../dashboard/components/StatTile";
import {ServiceNotConfiguredBanner} from "../components/ServiceNotConfiguredBanner";
import {TopicGallery} from "./components/TopicGallery";
import {TopicDetailsPanel} from "./components/TopicDetailsPanel";
import {CreateTopicModalNew} from "./components/CreateTopicModalNew";
import {
  projectAtom,
  subProjectAtom,
  topicsAtom,
  selectedTopicAtom,
  topicsLoadingAtom,
} from "./atoms";

export const KnowledgeManagerPageRegistry = ({project, subProject}) => {

  const [topics, setTopics] = useAtom(topicsAtom);
  const [selectedTopic, setSelectedTopic] = useAtom(selectedTopicAtom);
  const [loading, setLoading] = useAtom(topicsLoadingAtom);
  const setProjectAtom = useSetAtom(projectAtom);
  const setSubProjectAtom = useSetAtom(subProjectAtom);

  const [openCreate, setOpenCreate] = useState(false);

  // Sync route-driven project/language into atoms so child components can read them without prop drilling
  useEffect(() => {
    setProjectAtom(project);
  }, [project, setProjectAtom]);

  useEffect(() => {
    setSubProjectAtom(subProject);
  }, [subProject, setSubProjectAtom]);

  const fetchTopics = useCallback(async () => {
    if (!project?.id || !project?.backendConfiguration?.knowledgeManagerApi) return;
    try {
      setLoading(true);
      const list = await KnowledgeManagerRepository.listTopics({
        organizationId: project.organizationId,
        projectId: String(project.id),
      });
      setTopics(list);
    } catch (err) {
      toast.error(err.message || "Failed to load knowledge base topics");
      console.error("Error fetching topics:", err);
    } finally {
      setLoading(false);
    }
  }, [project?.id, project?.organizationId, project?.backendConfiguration?.knowledgeManagerApi, setLoading, setTopics]);

  useEffect(() => {
    fetchTopics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id]);

  // Clear selection when leaving the page so it doesn't leak into the next visit
  useEffect(() => {
    return () => setSelectedTopic(null);
  }, [setSelectedTopic]);

  const handleTopicCreated = async (topic) => {
    setOpenCreate(false);
    await fetchTopics();
    if (topic) setSelectedTopic(topic);
  };

  const totalTopics = topics.length;
  const totalDocuments = topics.reduce((sum, t) => sum + (t.documentCount || 0), 0);
  const totalQueries = topics.reduce((sum, t) => sum + (t.queryCount || 0), 0);
  const avgDocsPerTopic = totalTopics ? Math.round((totalDocuments / totalTopics) * 10) / 10 : 0;

  return (
    <Stack gap={3} sx={{
      p: {xs: 2, md: 3}, maxWidth: {xs: "100%", md: "calc(100% - 128px)"}, width: "100%", mx: "auto",
      boxSizing: "border-box",
    }}>

      <CreateTopicModalNew
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        onCreated={handleTopicCreated}
      />

      {!project?.backendConfiguration?.knowledgeManagerApi && (
        <ServiceNotConfiguredBanner label={"Knowledge Manager API"}/>
      )}

      {selectedTopic ? (
        <TopicDetailsPanel onTopicChanged={fetchTopics}/>
      ) : (
        <>
          <Stack direction={{xs: "column", md: "row"}} justifyContent={"space-between"} alignItems={{md: "flex-end"}} gap={2}>
            <Box>
              <Typography sx={{fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", color: "#8491F7", textTransform: "uppercase"}}>
                KNOWLEDGE BASE
              </Typography>
              <Typography component={"h1"} sx={{fontSize: 26, fontWeight: 700, color: "#23264D", letterSpacing: "-0.02em", mt: 0.5}}>
                Knowledge Manager
              </Typography>
              <Typography sx={{fontSize: 14, color: "#6B6B80", mt: 0.5}}>
                Organize topics, upload documents and query your project's knowledge base
              </Typography>
            </Box>
            <Stack direction="row" gap={1} alignItems="center">
              <Button
                size="small"
                startIcon={<HugeiconsIcon icon={Add01Icon} size={16}/>}
                onClick={() => setOpenCreate(true)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: 13,
                  borderRadius: '10px',
                  color: '#374151',
                  bgcolor: '#FAFAFD',
                  '&:hover': {bgcolor: '#ECECF3'},
                }}
              >
                New Topic
              </Button>
              <Tooltip title={"Refresh"}>
                <IconButton onClick={fetchTopics} disabled={loading} sx={{borderRadius: "50% !important", color: "#8A8AA0"}}>
                  <HugeiconsIcon icon={Refresh01Icon} size={17}/>
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>

          <StatStrip items={[
            {label: "Topics", value: totalTopics, note: totalTopics ? `Avg ${avgDocsPerTopic} docs per topic` : "No topics yet"},
            {label: "Documents", value: totalDocuments, note: "Indexed across all topics"},
            {label: "Queries", value: totalQueries, note: "Total questions asked"},
            {label: "Sub projects", value: project?.subProjects?.length || 0, note: "Available in this project"},
          ]}/>

          <TopicGallery onAddClick={() => setOpenCreate(true)}/>
        </>
      )}
    </Stack>
  );
};
